import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import DeleteSweepRounded from "@mui/icons-material/DeleteSweepRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import LayersClearOutlined from "@mui/icons-material/LayersClearOutlined";
import {
  CustomSnackbar,
  EmptyState,
  Loading,
  PageHeader,
} from "../components";
import { CustomSnackbarRef } from "../components/CustomSnackbar";
import { useData } from "../hooks/useData";
import {
  CascadeItem,
  CascadeRateKey,
  CascadeResponse,
  ProviderTerminal,
  getCascadeByRate,
  getRates,
  reorderCascade,
  deleteCascade,
  setCascade,
  toggleCascadeItem,
} from "../utils/api";
import { translate } from "../utils/currency";

/** Ставка мерчанта в том виде, в каком её отдаёт GET /rates (без merchant_id). */
type MerchantRate = {
  payment_method: string;
  operation_type: string;
  currency: string;
  rate: number;
  minsum: number;
  maxsum: number;
};

/** Строка каскада в локальном состоянии: терминал + флаг участия. */
type Row = ProviderTerminal & { enabled: boolean };

const money = (value: number | null) =>
  value === null || value === undefined
    ? "—"
    : Number(value).toLocaleString("ru-RU");

const rateLabel = (r: MerchantRate) =>
  `${translate(r.payment_method)} · ${r.operation_type} · ${r.currency.toUpperCase()} · ${money(
    r.minsum,
  )}–${money(r.maxsum)} · ${r.rate}%`;

const CascadesPage: React.FC = () => {
  const theme = useTheme();
  const snackbarRef = useRef<CustomSnackbarRef>(null);
  const { merchants } = useData();

  const [ratesByMerchant, setRatesByMerchant] = useState<
    Record<string, MerchantRate[]>
  >({});
  const [merchantId, setMerchantId] = useState<number | "">("");
  const [rateIndex, setRateIndex] = useState<number | "">("");

  const [data, setData] = useState<CascadeResponse | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loadingRates, setLoadingRates] = useState(true);
  const [loadingCascade, setLoadingCascade] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const notify = (
    message: string,
    severity: "success" | "error" | "info" | "warning",
  ) => snackbarRef.current?.showSnackbar(message, severity);

  /** Достаёт человекочитаемый текст из detail, который бывает и строкой, и объектом. */
  const describeError = (err: any): string => {
    const detail = err?.response?.data?.detail;
    if (!detail) return "Не удалось выполнить запрос";
    if (typeof detail === "string") return detail;
    if (detail.message) {
      const ids = detail.provider_terminal_ids ?? detail.current_terminal_ids;
      return ids?.length ? `${detail.message}: ${ids.join(", ")}` : detail.message;
    }
    return "Не удалось выполнить запрос";
  };

  useEffect(() => {
    setLoadingRates(true);
    getRates()
      .then((res) => setRatesByMerchant(res.data ?? {}))
      .catch(() => setError("Не удалось загрузить ставки мерчантов"))
      .finally(() => setLoadingRates(false));
  }, []);

  const merchantRates = useMemo<MerchantRate[]>(
    () => (merchantId === "" ? [] : (ratesByMerchant[String(merchantId)] ?? [])),
    [ratesByMerchant, merchantId],
  );

  const selectedRate =
    rateIndex === "" ? null : (merchantRates[rateIndex] ?? null);

  /**
   * Ключ каскада — те же шесть полей, что и у ставки. Бэкенд ищет rate по
   * точному совпадению, поэтому суммы отдаём ровно как пришли из GET /rates.
   */
  const rateKey = useMemo<CascadeRateKey | null>(() => {
    if (merchantId === "" || !selectedRate) return null;
    return {
      merchant_id: Number(merchantId),
      payment_method: selectedRate.payment_method,
      operation_type: selectedRate.operation_type,
      currency: selectedRate.currency,
      minsum: selectedRate.minsum,
      maxsum: selectedRate.maxsum,
    };
  }, [merchantId, selectedRate]);

  const load = useCallback(async (key: CascadeRateKey) => {
    setLoadingCascade(true);
    setError(null);
    try {
      const res = await getCascadeByRate(key);
      const payload: CascadeResponse = res.data;
      setData(payload);
      setRows(
        payload.cascade
          .slice()
          .sort((a, b) => a.priority - b.priority)
          .map((item: CascadeItem) => ({ ...item, enabled: item.enabled })),
      );
    } catch (err: any) {
      setData(null);
      setRows([]);
      setError(describeError(err));
    } finally {
      setLoadingCascade(false);
    }
  }, []);

  useEffect(() => {
    if (rateKey) load(rateKey);
    else {
      setData(null);
      setRows([]);
    }
  }, [rateKey, load]);

  /** Терминалы, подходящие под ставку, но ещё не добавленные в каскад. */
  const available = useMemo(() => {
    const used = new Set(rows.map((r) => r.provider_terminal_id));
    return (data?.eligible_provider_terminals ?? []).filter(
      (t) => !used.has(t.provider_terminal_id),
    );
  }, [data, rows]);

  const dirty = useMemo(() => {
    if (!data) return false;
    const saved = data.cascade
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((c) => `${c.provider_terminal_id}:${c.enabled}`)
      .join(",");
    const current = rows
      .map((r) => `${r.provider_terminal_id}:${r.enabled}`)
      .join(",");
    return saved !== current;
  }, [data, rows]);

  const move = (index: number, delta: number) => {
    setRows((prev) => {
      const next = prev.slice();
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addTerminal = (terminal: ProviderTerminal) =>
    setRows((prev) => [...prev, { ...terminal, enabled: true }]);

  const removeRow = (terminalId: number) =>
    setRows((prev) =>
      prev.filter((r) => r.provider_terminal_id !== terminalId),
    );

  /**
   * Тумблер шлём точечным PATCH только для терминалов, уже сохранённых в
   * каскаде, — иначе бэкенд не найдёт cascade item. Для новых строк просто
   * держим состояние локально до сохранения.
   */
  const handleToggle = async (row: Row, enabled: boolean) => {
    setRows((prev) =>
      prev.map((r) =>
        r.provider_terminal_id === row.provider_terminal_id
          ? { ...r, enabled }
          : r,
      ),
    );

    const isSaved = data?.cascade.some(
      (c) => c.provider_terminal_id === row.provider_terminal_id,
    );
    if (!rateKey || !isSaved) return;

    try {
      await toggleCascadeItem(rateKey, row.provider_terminal_id, enabled);
      notify(
        `${row.provider_name}: ${enabled ? "включён" : "выключен"}`,
        "success",
      );
      await load(rateKey);
    } catch (err) {
      notify(describeError(err), "error");
      await load(rateKey);
    }
  };

  const handleDropCascade = async () => {
    if (!rateKey) return;
    setSaving(true);
    try {
      const res = await deleteCascade(rateKey);
      notify(
        `Каскад снят: удалено шагов — ${res.data?.deleted_items ?? 0}`,
        "success",
      );
      setDropOpen(false);
      await load(rateKey);
    } catch (err) {
      notify(describeError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!rateKey) return;
    setSaving(true);
    try {
      await setCascade(
        rateKey,
        rows.map((r, index) => ({
          provider_terminal_id: r.provider_terminal_id,
          priority: index + 1,
          enabled: r.enabled,
        })),
      );
      notify(`Каскад сохранён: ${rows.length} терминал(ов)`, "success");
      await load(rateKey);
    } catch (err) {
      notify(describeError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Отдельная кнопка на случай, когда состав каскада не менялся: reorder
   * дешевле полной замены и требует ровно текущий набор терминалов.
   */
  const handleReorder = async () => {
    if (!rateKey) return;
    setSaving(true);
    try {
      await reorderCascade(
        rateKey,
        rows.map((r) => r.provider_terminal_id),
      );
      notify("Порядок сохранён", "success");
      await load(rateKey);
    } catch (err) {
      notify(describeError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const sameComposition = useMemo(() => {
    if (!data) return false;
    const saved = new Set(data.cascade.map((c) => c.provider_terminal_id));
    return (
      saved.size === rows.length &&
      rows.every((r) => saved.has(r.provider_terminal_id))
    );
  }, [data, rows]);

  if (loadingRates) return <Loading variant="cards" />;

  return (
    <Box
      sx={{
        width: "100%",
        paddingTop: "25px",
        maxHeight: {
          xs: "calc(var(--content-max-height) + 32.5dvh)",
          md: "82vh",
        },
        overflow: "auto",
      }}
    >
      <Box sx={{ width: "96%", margin: "0 auto", pb: 3 }}>
        <CustomSnackbar duration={3500} ref={snackbarRef} />

        <PageHeader
          title="Каскады провайдеров"
          description="Очередь терминалов под ставку мерчанта: порядок и участие в запросах"
          icon={AccountTreeOutlined}
          actions={
            data && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltRounded />}
                  disabled={!dirty || saving}
                  onClick={() => rateKey && load(rateKey)}
                >
                  Сбросить
                </Button>
                {sameComposition && (
                  <Button
                    variant="outlined"
                    disabled={!dirty || saving}
                    onClick={handleReorder}
                  >
                    Только порядок
                  </Button>
                )}
                {data.cascade.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepRounded />}
                    disabled={saving}
                    onClick={() => setDropOpen(true)}
                  >
                    Снять каскад
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<SaveRounded />}
                  disabled={!dirty || saving}
                  onClick={handleSave}
                >
                  Сохранить каскад
                </Button>
              </>
            )
          }
        />

        <Paper sx={{ p: 2.5, mb: 2.5, borderRadius: "18px" }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl sx={{ minWidth: 240, flex: 1 }}>
              <InputLabel id="cascade-merchant">Мерчант</InputLabel>
              <Select
                labelId="cascade-merchant"
                label="Мерчант"
                value={merchantId}
                onChange={(e) => {
                  setMerchantId(Number(e.target.value));
                  setRateIndex("");
                }}
              >
                {merchants.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 320, flex: 2 }} disabled={merchantId === ""}>
              <InputLabel id="cascade-rate">Ставка</InputLabel>
              <Select
                labelId="cascade-rate"
                label="Ставка"
                value={rateIndex}
                onChange={(e) => setRateIndex(Number(e.target.value))}
              >
                {merchantRates.map((r, index) => (
                  <MenuItem
                    key={`${r.payment_method}-${r.operation_type}-${r.currency}-${r.minsum}-${r.maxsum}`}
                    value={index}
                  >
                    {rateLabel(r)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {merchantId !== "" && merchantRates.length === 0 && (
            <Typography color="text.secondary" sx={{ mt: 2, fontSize: 14 }}>
              У мерчанта нет настроенных ставок — каскад привязывается к ставке,
              так что сначала её нужно создать.
            </Typography>
          )}
        </Paper>

        {loadingCascade ? (
          <Loading compact variant="table" />
        ) : error ? (
          <EmptyState
            icon={LayersClearOutlined}
            title="Каскад недоступен"
            description={error}
            compact
          />
        ) : !data ? (
          <EmptyState
            icon={AccountTreeOutlined}
            title="Выберите мерчанта и ставку"
            description="Каскад настраивается под конкретную ставку: метод, операция, валюта и диапазон сумм."
            compact
          />
        ) : (
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5}>
            <Paper sx={{ p: 2.5, borderRadius: "18px", flex: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="h6" sx={{ fontSize: 17 }}>
                  Очередь запросов
                </Typography>
                <Chip
                  size="small"
                  label={`${rows.filter((r) => r.enabled).length} из ${rows.length} активны`}
                  sx={{ fontWeight: 700 }}
                />
              </Stack>

              {rows.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontSize: 14, py: 3 }}>
                  Каскад пуст. Добавьте терминалы справа — первый в списке
                  получит запрос первым.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {rows.map((row, index) => (
                    <Box
                      key={row.provider_terminal_id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: "14px",
                        border: `1px solid ${theme.palette.divider}`,
                        opacity: row.enabled ? 1 : 0.55,
                        backgroundColor: row.enabled
                          ? alpha(theme.palette.primary.main, 0.04)
                          : "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          flexShrink: 0,
                          borderRadius: "10px",
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 800,
                          fontSize: 14,
                          color: theme.palette.accentText,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.12,
                          ),
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 650, fontSize: 14.5 }} noWrap>
                          {row.provider_name}
                        </Typography>
                        <Typography
                          color="text.secondary"
                          sx={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
                          noWrap
                        >
                          terminal #{row.provider_terminal_id} · {row.rate}% ·{" "}
                          {money(row.minsum)}–{money(row.maxsum)}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Выше">
                          <span>
                            <IconButton
                              aria-label="Поднять терминал"
                              size="small"
                              disabled={index === 0}
                              onClick={() => move(index, -1)}
                            >
                              <ArrowUpwardRounded fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Ниже">
                          <span>
                            <IconButton
                              aria-label="Опустить терминал"
                              size="small"
                              disabled={index === rows.length - 1}
                              onClick={() => move(index, 1)}
                            >
                              <ArrowDownwardRounded fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={
                            row.enabled
                              ? "Участвует в очереди"
                              : "Сохранён, но запросы не идут"
                          }
                        >
                          <Switch
                            size="small"
                            checked={row.enabled}
                            onChange={(e) => handleToggle(row, e.target.checked)}
                            inputProps={{ "aria-label": "Участие в каскаде" }}
                          />
                        </Tooltip>

                        <Tooltip title="Убрать из каскада">
                          <IconButton
                            aria-label="Убрать из каскада"
                            size="small"
                            onClick={() => removeRow(row.provider_terminal_id)}
                            sx={{ color: theme.palette.semanticText.error }}
                          >
                            <RemoveRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper sx={{ p: 2.5, borderRadius: "18px", flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: 17, mb: 0.5 }}>
                Доступные терминалы
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12.5, mb: 2 }}>
                Подходят по методу, операции, валюте и диапазону сумм
              </Typography>

              {available.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                  {data.eligible_provider_terminals.length === 0
                    ? "Под эту ставку нет подходящих терминалов провайдеров."
                    : "Все подходящие терминалы уже в каскаде."}
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {available.map((t) => (
                    <Box
                      key={t.provider_terminal_id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: "14px",
                        border: `1px dashed ${theme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 650, fontSize: 14 }} noWrap>
                          {t.provider_name}
                        </Typography>
                        <Typography
                          color="text.secondary"
                          sx={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
                          noWrap
                        >
                          #{t.provider_terminal_id} · {t.rate}% ·{" "}
                          {money(t.minsum)}–{money(t.maxsum)}
                        </Typography>
                      </Box>
                      <Tooltip title="Добавить в каскад">
                        <IconButton
                          aria-label="Добавить в каскад"
                          size="small"
                          onClick={() => addTerminal(t)}
                          sx={{ color: theme.palette.semanticText.success }}
                        >
                          <AddRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}

        <Dialog
          open={dropOpen}
          onClose={() => !saving && setDropOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Снять каскад?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Все шаги очереди будут удалены с этой ставки. После этого платежи
              по ней пойдут прежней логикой подбора провайдера — либо перестанут
              проходить, если её для этого метода уже нет.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDropOpen(false)} disabled={saving}>
              Отмена
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDropCascade}
              disabled={saving}
            >
              Снять
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CascadesPage;
